// Éléments partagés du module décision : libellés, constats, actions avec retour, dates.
import { useState, type ReactNode } from "react";
import { useInvalidate } from "@core/hooks";
import { dateLongFmt } from "@core/format";
import { Source, StatusChip, useToast, type CheckStatus } from "@core/ui";

export type Check = {
  id: string;
  applicant_id: string | null;
  document_id: string | null;
  family: string;
  code: string;
  status: CheckStatus;
  title: string;
  explanation: string;
  sources: { type?: string; label?: string; date?: string }[];
  created_at: string;
};

export type Person = {
  id: string;
  role: "titulaire" | "cotitulaire" | "garant";
  guarantor_of: string | null;
  first_name: string;
  last_name: string;
  email: string | null;
  situation: string;
  declared_monthly_income_cents: number | null;
};

export const fullName = (p: { first_name: string; last_name: string }) => `${p.first_name} ${p.last_name}`.trim();

export const ROLE_LABELS: Record<string, string> = { titulaire: "Titulaire", cotitulaire: "Cotitulaire", garant: "Garant" };

export const OUTCOME_LABELS: Record<string, string> = { retenue: "Retenu", refusee: "Refusé", liste_attente: "Liste d’attente" };

export const APPLICATION_STATUS: Record<string, string> = {
  brouillon: "Brouillon",
  deposee: "Déposée",
  a_completer: "À compléter",
  en_verification: "En vérification",
  verifiee: "Vérifiée",
  retenue: "Retenue",
  refusee: "Refusée",
  liste_attente: "Liste d’attente",
  retiree: "Retirée",
};

/** Décision affichée avec un mot et une icône (jamais la couleur seule). */
export function OutcomeChip({ outcome }: { outcome: string }) {
  const status: CheckStatus = outcome === "retenue" ? "conforme" : outcome === "refusee" ? "manquant" : "info";
  return <StatusChip status={status} label={OUTCOME_LABELS[outcome] ?? outcome} />;
}

/** Constat expliqué : statut, titre, explication et source datée. */
export function CheckLine({ check, extra }: { check: Check; extra?: ReactNode }) {
  const src = check.sources?.[0];
  return (
    <article className="bv-constat decision-constat">
      <div className="bv-constat-tete">
        <h3 className="bv-constat-titre decision-constat-titre">{check.title}</h3>
        <StatusChip status={check.status} />
      </div>
      <p>{check.explanation}</p>
      <div className="bv-constat-pied">
        <Source doc={src?.label ?? "Source non précisée"} details={src?.date ? [dateLongFmt(src.date)] : []} />
        {extra}
      </div>
    </article>
  );
}

/** Exécute une action serveur avec retour visible et rafraîchissement des données concernées. */
export function useAction(prefixes: string[]) {
  const toast = useToast();
  const invalidate = useInvalidate();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  async function run<T>(key: string, fn: () => Promise<T>, success?: string): Promise<T | undefined> {
    setBusy(key);
    setError(null);
    try {
      const out = await fn();
      if (success) toast(success);
      await invalidate(...prefixes);
      return out;
    } catch (err) {
      setError(err);
      toast(err instanceof Error ? err.message : "Action impossible.", "erreur");
      return undefined;
    } finally {
      setBusy(null);
    }
  }
  return { busy, error, setError, run };
}

/** Lien vers un site officiel, ouvert dans un nouvel onglet. */
export function OfficialLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noreferrer noopener" className="decision-lien-officiel">
      {children}
      <span className="sr-only"> (site officiel, nouvel onglet)</span>
    </a>
  );
}

export const docUrl = (id: string) => `/api/v1/documents/${id}/fichier`;

/** Choix exclusif accessible (boutons radio avec libellés visibles). */
export function ChoiceGroup<T extends string>({ legend, value, onChange, options, name }: { legend: string; value: T | ""; onChange: (v: T) => void; options: { value: T; label: string; help?: string }[]; name: string }) {
  return (
    <fieldset className="decision-choix">
      <legend>{legend}</legend>
      {options.map((o) => (
        <label key={o.value} className={`decision-choix-option ${value === o.value ? "decision-choix-actif" : ""}`}>
          <input type="radio" name={name} value={o.value} checked={value === o.value} onChange={() => onChange(o.value)} />
          <span>
            <strong>{o.label}</strong>
            {o.help ? <span className="block small muted">{o.help}</span> : null}
          </span>
        </label>
      ))}
    </fieldset>
  );
}
